import { useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

export type NormalizedBox = { x: number; y: number; w: number; h: number };

type Props = {
  src: string;
  box: NormalizedBox | null;
  onChange: (box: NormalizedBox) => void;
  maxWidth?: number;
};

export default function BoxSelector({ src, box, onChange, maxWidth = 420 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);

  const pointToFraction = (event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = containerRef.current!.getBoundingClientRect();
    const x = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    const y = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1);
    return { x, y };
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragStart(pointToFraction(event));
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStart) return;
    const current = pointToFraction(event);
    onChange({
      x: Math.min(dragStart.x, current.x),
      y: Math.min(dragStart.y, current.y),
      w: Math.abs(current.x - dragStart.x),
      h: Math.abs(current.y - dragStart.y),
    });
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={() => setDragStart(null)}
      style={{
        position: "relative",
        display: "inline-block",
        width: "100%",
        maxWidth,
        lineHeight: 0,
        borderRadius: 8,
        border: "1px solid var(--line)",
        background: "var(--bg-2)",
        cursor: "crosshair",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <img src={src} draggable={false} style={{ width: "100%", height: "auto", display: "block", pointerEvents: "none" }} />
      {box && box.w > 0 && box.h > 0 && (
        <div
          style={{
            position: "absolute",
            left: `${box.x * 100}%`,
            top: `${box.y * 100}%`,
            width: `${box.w * 100}%`,
            height: `${box.h * 100}%`,
            border: "2px solid #ff2d2d",
            boxShadow: "0 0 0 1px rgba(255,255,255,.7)",
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}
